<script setup>
definePage({
  meta: {
    action: 'read',
    subject: 'build',
  },
})

import { useBuildStore } from "@/utils/stores/build"
import { useESignatureStore } from "@/utils/stores/esignature"
import { useProjectStore } from "@/utils/stores/project"
import ESignature from "@/views/build/common/eSignature.vue"
import CrfGroupPage from "@/views/build/crf-group-page/index.vue"

// import Entree from "@/views/build/crf-page/entree.vue"
import { useAlertDialog } from '@/utils/stores/alertDialog'
import { useLoading } from '@/utils/stores/loading'
import CycleVisit from "@/views/build/cycle-visit/index.vue"
import Schedule from "@/views/build/schedule/index.vue"
import ConfirmDialogCustom from "@/components/dialogs/ConfirmDialogCustom.vue"
import EcrfVersionSaveDialog from "@/components/dialogs/EcrfVersionSaveDialog.vue"
import EcrfVersionHistoryDialog from "@/components/dialogs/EcrfVersionHistoryDialog.vue"
import EcrfVersionSelectDialog from "@/components/dialogs/EcrfVersionSelectDialog.vue"

const currentTab = ref(null)

const loadingStore = useLoading()
const esignatureStore = useESignatureStore()
const alertDialogStore = useAlertDialog()
const buildStore = useBuildStore()
const projectStore = useProjectStore()
const { isBuild } = storeToRefs(buildStore)

const projectLocked = ref(true)
const projectAnswered = ref(true)
const projectStatus = ref('ready')
const versionSaveDialogVisible = ref(false)
const versionHistoryDialogVisible = ref(false)
const blankVersionSelectVisible = ref(false)
const applyConfirmVisible = ref(false)

// Backend에서 데이터 가져오기.
// get build data - schedule, cyclevisit, crfgroup, crfPage
// tab들은 pinia에서 동기화. save하면 DB에 저장하고...
// build 는 그때 저장하는 걸로.
buildStore.isBuild = false
buildStore.crfPages = {}


const { data: project, error } = await useApi(createUrl(`/projects/${projectStore.projectId}`, {
  query: {
  },
}))



const loadBuild = async() => {
  const { data, error } = await useApi(createUrl('/build', {
    query: {
      projectId: projectStore.projectId,
    },
  }))

  if (error.value) {
    console.error(error.value)
  } else {
    if (data.value) {
      console.log(data.value)
      buildStore.crfGroup = (data.value.crfGroup)
      buildStore.cycleVisit = (data.value.cycleVisit)
      buildStore.schedule = (data.value.schedule)
      buildStore.uvSchedule = (data.value.uvSchedule)
      if (data.value && data.value.project) {
        buildStore.standard = Array.isArray(data.value.project.standard)
          ? data.value.project.standard // 이미 배열이면 그대로 사용
          : JSON.parse(data.value.project.standard) // 문자열이면 JSON 변환
      } else{
        buildStore.standard = []
      }

      projectLocked.value = !!(data.value.project.locked == 1)
      projectAnswered.value = !!(data.value.answers > 0)
      projectStatus.value = data.value.project?.project_status ?? 'ready'
    }
  }
}

loadBuild()

const toggleBuild = () =>{

  // modal
  esignatureStore.$reset()
  esignatureStore.isDialogVisible = true

  // buildStore.isBuild = !isBuild.value
}

const onApplyClick = () => {
  applyConfirmVisible.value = true
}

const onApplyConfirm = (confirmed) => {
  if (confirmed) {
    toggleBuild()
  }
}

const signConfirm = async ({ result }) => {
  if (!result) return
  if (buildStore.isBuild) {
    if (projectStatus.value !== 'ready') {
      versionSaveDialogVisible.value = true
      
      return
    }
    await doSaveBuild({})
  } else {
    buildStore.isBuild = true
  }
}

async function doSaveBuild({ reason = '' } = {}) {
  // crfPage를 먼저 저장 (스냅샷이 최신 데이터를 반영하도록)
  const crfPageEntries = Object.entries(buildStore.crfPages || {})
  for (const [groupId, crfPage] of crfPageEntries) {
    await $api('/crf-page', {
      method: 'POST',
      body: {
        projectId: projectStore.projectId,
        crfGroupId: Number(groupId),
        crfPage,
      },
    })
  }

  const body = {
    projectId: projectStore.projectId,
    crfGroup: buildStore.crfGroup,
    cycleVisit: buildStore.cycleVisit,
    schedule: buildStore.schedule,
    uvSchedule: buildStore.uvSchedule,
  }

  if (reason) body.reason = reason
  const res = await $api('/build', { method: 'POST', body })
  if (res?.code === 200) {
    buildStore.crfPages = {}
    buildStore.isBuild = false
    await loadBuild()
    alertDialogStore.showAlertDialog('저장되었습니다.')
  } else if (res?.message) {
    alertDialogStore.showAlertDialog(res.message, 'error')
  }
}

function onVersionSaveConfirm({ reason }) {
  versionSaveDialogVisible.value = false
  doSaveBuild({ reason })
}

const downloadBlankPDF = async (versionId = null) => {
  blankVersionSelectVisible.value = false
  loadingStore.message = "요청중... 기다려주세요."
  loadingStore.isLoading = true
  try {
    const body = { projectId: projectStore.projectId }
    if (versionId != null) body.versionId = versionId
    await $api('/blank-crf', { method: 'POST', responseType: 'blob', body })
    alertDialogStore.showAlertDialog('요청이 완료되었습니다. 결과는 이메일로 전달됩니다.')
  } catch (e) {
    alertDialogStore.showAlertDialog('요청에 실패했습니다.', 'error')
  } finally {
    loadingStore.isLoading = false
  }
}

function onBlankVersionSelect(versionId) {
  downloadBlankPDF(versionId)
}
</script>

<template>
  <h3 class="text-lg font-semibold mb-8">
    문서 개발
  </h3>
  <ConfirmDialogCustom
    v-model="applyConfirmVisible"
    confirmation-question="스케줄 등 모든 설정을 완료 하였습니까?"
    confirm-msg=""
    cancel-msg=""
    @confirm="onApplyConfirm"
  />
  <ESignature @confirm="signConfirm" />
  <EcrfVersionSaveDialog
    v-model:visible="versionSaveDialogVisible"
    @confirm="onVersionSaveConfirm"
  />
  <EcrfVersionHistoryDialog v-model:visible="versionHistoryDialogVisible" />
  <EcrfVersionSelectDialog
    v-model:visible="blankVersionSelectVisible"
    @confirm="onBlankVersionSelect"
  />
  <VRow>
    <VCol class="text-right">
      <VBtn
        style="z-index: 10;background-color: #444C74 !important;inline-size: 90px !important; text-transform: none !important; "
        color="success"
        class="mr-2"
        @click="blankVersionSelectVisible = true"
      >
        Blank eCRF
      </VBtn>
      <VBtn
        style="z-index: 10; text-transform: none !important;"
        variant="tonal"
        class="mr-2"
        @click="versionHistoryDialogVisible = true"
      >
        변경이력
      </VBtn>
      <VBtn
        v-if="isBuild"
        style="z-index: 10;"
        type="button"
        @click="onApplyClick"
      >
        적용
      </VBtn>
      <VBtn
        v-if="!isBuild && $can('write', 'build')"
        style="z-index: 10;"
        type="button"
        :disabled="projectLocked"
        @click="toggleBuild"
      >
        <!-- || projectAnswered -->
        해제
        <VIcon
          v-if="projectLocked"
          end
          icon="tabler-lock"
        />
        <VIcon
          v-if="!projectLocked && projectAnswered"
          end
          icon="tabler-pencil"
        />
      </VBtn>
      <span><h4> &nbsp;</h4></span>
    </VCol>
  </VRow>
  <VRow style="position: relative;inset-block-start:-80px;">
    <VCol>
      <VTabs
        v-model="currentTab"
        class="v-tabs-pill"
      >
        <VTab>{{ $t('CRF Group Page') }}</VTab>  
        <VTab>{{ $t('Cycle') }} & {{ $t('Visit') }}</VTab>
        <VTab>{{ $t('Schedule') }}</VTab>
      </VTabs>

      <VWindow
        v-model="currentTab"
        class="disable-tab-transition"
      >
        <VWindowItem key="window3">
          <CrfGroupPage />
        </VWindowItem>  
        <VWindowItem key="window2">
          <CycleVisit />
        </VWindowItem>
        
        <VWindowItem key="window1">
          <Schedule />
        </VWindowItem>
      </VWindow>
    </VCol>
  </VRow>
</template>



<style lang="scss" scoped> 
// 👉 Tab Pill
.v-tabs.v-tabs-pill {
  .v-slide-group-item--active.v-tab--selected {
    background-color: #6A78D3;
    color: white;
    font-weight: bold;
  }
}

.v-tabs.v-tabs-pill .v-tab.v-btn{
  border : 1px solid #6A78D3;
  color: #2F45B8;
  margin-inline-end: 10px;
}
</style>
